import {useEffect} from 'react';
import useGetVimeoVideo from '@src/Api/TraceApi/Hooks/useGetVimeoVideo';
import {useGetLanguagesQuery} from '@src/Api/Umbraco/ContentManagmentApi/contentManagmentApi';
import PageLoader from '@src/components/general/PageLoader/PageLoader';
import ThemeProvider from 'design-system/src/Provider/ThemeProvider/ThemeProvider';
import VimeoProvider from 'design-system/src/Provider/VimeoProvider/VimeoProvider';
import defaultTheme from 'design-system/src/Theme/defaultTheme';
import GridGlobalCss from 'design-system/src/WebComponent/Grid/GridGlobalCss';
import React from 'react';
import {RadioProvider} from '../RadioProvider/RadioProvider';
import {FloatingListenProvider} from '../FloatingListenProvider/FloatingListenProvider';
import {PlayListProvider} from '../PlayListProvider/PlayListProvider';
import useInitSetting from '@src/Services/Query/hooks/useInitSetting';
import AuthProvider from '../AuthProvider/AuthProvider';
import {SessionProvider} from '../SessionProvider';
import DebugModeProvider from 'design-system/src/Provider/DebugModeProvider/DebugModeProvider';
import FirebaseRemoteConfigProvider from '../FirebaseRemoteConfigProvider/FirebaseRemoteConfigProvider';
import AnonymousAuthProvider from '../AnonymousAuthProvider/AnonymousAuthProvider';
import AuthGuardProvider from '../AuthGuardProvider/AuthGuardProvider';
import PaywallProvider from '../PaywallProvider/PaywallProvider';
import {useVodacomRedirect} from '@src/Hooks/useVodacomRedirect';
import {useVodacomAutoLogin} from '@src/Hooks/useVodacomAutoLogin';
import {useVodacomSessionRefresh} from '@src/Hooks/useVodacomSessionRefresh';

interface IInnerProviderProps {
  children: React.ReactElement;
}

const VodacomAutoLoginHandler = ({
  children,
}: {
  children: React.ReactElement;
}) => {
  const {isProcessing} = useVodacomAutoLogin();
  useVodacomSessionRefresh();

  if (isProcessing) {
    return <PageLoader />;
  }

  return children;
};

const InnerProvider = (props: IInnerProviderProps) => {
  const {children} = props;
  const {isLoading} = useGetLanguagesQuery();
  const {getVimeoVideo} = useGetVimeoVideo();

  const {init} = useInitSetting();

  useEffect(() => {
    init();
  }, []);

  useVodacomRedirect();

  return (
    <DebugModeProvider>
      <ThemeProvider
        theme={{
          ...defaultTheme,
        }}>
        <GridGlobalCss />
        <VimeoProvider
          value={{
            getVimeoVideo,
          }}>
          <RadioProvider>
            <PlayListProvider>
              <FloatingListenProvider>
                <SessionProvider>
                  <FirebaseRemoteConfigProvider>
                    <AnonymousAuthProvider>
                      <PaywallProvider>
                        <AuthGuardProvider>
                          <AuthProvider>
                            <VodacomAutoLoginHandler>
                              {isLoading ? <PageLoader /> : children}
                            </VodacomAutoLoginHandler>
                          </AuthProvider>
                        </AuthGuardProvider>
                      </PaywallProvider>
                    </AnonymousAuthProvider>
                  </FirebaseRemoteConfigProvider>
                </SessionProvider>
              </FloatingListenProvider>
            </PlayListProvider>
          </RadioProvider>
        </VimeoProvider>
      </ThemeProvider>
    </DebugModeProvider>
  );
};

export default React.memo(InnerProvider);
